import React, { useState, useEffect, useCallback } from 'react';
import IvrCreateModal from '../../components/callflow/IvrCreateModal';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import SubAccountLayout, { useSubAccount } from '../../components/SubAccountLayout';
import { useTheme } from '../../context/ThemeContext';
import { toast } from 'sonner';
import {
  GitBranch, Plus, Search, Trash2, Loader2, Phone,
  ToggleLeft, ToggleRight, ArrowRight, X
} from 'lucide-react';

/* ─────────────────────────────────────────────────────────────────────────
   Call Routing — flow list
   This page is intentionally lean: list + create. Building/editing the
   actual flow happens entirely on the visual Canvas (`CallFlowCanvasPage`),
   so clicking any row navigates straight there.
   ───────────────────────────────────────────────────────────────────── */
const NewFlowModal = ({ onClose, onCreate, phoneNumbers, isDark }) => {
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState(phoneNumbers[0]?.phone_number || '');
  const [busy, setBusy] = useState(false);

  const inp = isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200 text-gray-900';

  const create = async () => {
    if (!name.trim() || !phoneNumber) { toast.error('Name and number required'); return; }
    setBusy(true);
    try {
      await onCreate({ name: name.trim(), phone_number: phoneNumber });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="new-flow-modal">
      <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="w-[440px] max-w-full bg-[#0c0d12] border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
        <div className="px-5 py-4 border-b border-white/10 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-teal-400/15 border border-teal-400/30 flex items-center justify-center">
            <GitBranch size={16} className="text-teal-300" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-white">New Routing Flow</div>
            <div className="text-[10px] text-gray-400">You'll build it visually on the canvas</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1" data-testid="new-flow-close"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-400 mb-1.5">Flow name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Office Hours"
              className={`w-full px-3 py-2 rounded-lg border ${inp} text-sm focus:outline-none focus:border-teal-400/50`}
              data-testid="new-flow-name" autoFocus />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-400 mb-1.5">Twilio number</label>
            <select value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)}
              className={`w-full px-3 py-2 rounded-lg border ${inp} text-sm focus:outline-none focus:border-teal-400/50`}
              data-testid="new-flow-phone">
              {phoneNumbers.length === 0 && <option value="">No numbers — buy one in Phone Numbers first</option>}
              {phoneNumbers.map((p) => (
                <option key={p.phone_number} value={p.phone_number}>
                  {p.phone_number} {p.label ? `· ${p.label}` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-white/10 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-gray-300 text-xs hover:text-white">Cancel</button>
          <button onClick={create} disabled={busy || !name.trim() || !phoneNumber}
            className="px-4 py-2 rounded-lg bg-teal-400 text-[#08090d] text-xs font-bold hover:bg-teal-500 disabled:opacity-50"
            data-testid="new-flow-create">
            {busy ? <Loader2 size={13} className="animate-spin inline mr-1" /> : null}
            Create + Open Canvas
          </button>
        </div>
      </motion.div>
    </div>
  );
};

const CallRoutingContent = () => {
  const { subAccountId, workspaceId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const { isDarkMode } = useTheme();
  useSubAccount();

  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showNewFlow, setShowNewFlow] = useState(false);

  const isDark = isDarkMode;
  const text1 = isDark ? 'text-white' : 'text-gray-900';
  const text2 = isDark ? 'text-gray-400' : 'text-gray-500';
  const bg = isDark ? 'bg-[#0c0d12]/90' : 'bg-white/90';
  const border = isDark ? 'border-white/[0.08]' : 'border-gray-200';
  const bgMain = isDark ? 'bg-[#08090d]' : 'bg-gray-50';
  const inputBg = isDark ? 'bg-white/5' : 'bg-gray-50';

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [f, p] = await Promise.all([
        api.get(`/api/sub-accounts/${subAccountId}/call-routing`),
        api.get(`/api/sub-accounts/${subAccountId}/phone-numbers`),
      ]);
      setFlows(Array.isArray(f.data) ? f.data : []);
      setPhoneNumbers(Array.isArray(p.data) ? p.data : []);
    } catch (err) {
      // empty list is fine
    } finally {
      setLoading(false);
    }
  }, [api, subAccountId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const [ivrModal, setIvrModal] = useState(null);
  const handleCreate = async ({ name, phone_number }) => {
    try {
      const r = await api.post(`/api/sub-accounts/${subAccountId}/call-routing`, {
        name, phone_number, mode: 'sequential', ring_timeout: 20,
      });
      const flow = r.data;
      toast.success('Flow created');
      setShowNewFlow(false);
      // Open the canvas immediately so the user is in the right place
      navigate(`/workspace/${flow.workspace_id}/sub-account/${subAccountId}/call-routing/${flow.flow_id}/canvas`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create flow');
    }
  };

  const handleToggle = async (flow, e) => {
    e.stopPropagation();
    try {
      await api.put(`/api/sub-accounts/${subAccountId}/call-routing/${flow.flow_id}`, { enabled: !flow.enabled });
      setFlows(prev => prev.map(f => f.flow_id === flow.flow_id ? { ...f, enabled: !f.enabled } : f));
      toast.success(flow.enabled ? 'Flow disabled' : 'Flow enabled');
    } catch { toast.error('Toggle failed'); }
  };

  const handleDelete = async (flow, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${flow.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/sub-accounts/${subAccountId}/call-routing/${flow.flow_id}`);
      setFlows(prev => prev.filter(f => f.flow_id !== flow.flow_id));
      toast.success('Deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const openCanvas = (flow) => {
    navigate(`/workspace/${flow.workspace_id}/sub-account/${subAccountId}/call-routing/${flow.flow_id}/canvas`);
  };

  const filtered = flows.filter(f => {
    const matchSearch = !search ||
      f.name?.toLowerCase().includes(search.toLowerCase()) ||
      f.phone_number?.includes(search);
    const matchStatus = statusFilter === 'all' ||
      (statusFilter === 'active' && f.enabled) ||
      (statusFilter === 'inactive' && !f.enabled);
    return matchSearch && matchStatus;
  });

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-teal-400" /></div>;
  }

  return (
    <div className={`min-h-screen ${bgMain} p-6 lg:p-8`} data-testid="call-routing-page">
      {isDark && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-0 left-1/3 w-[500px] h-[500px] bg-teal-400/5 rounded-full blur-[100px]" />
        </div>
      )}

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className={`text-2xl font-semibold ${text1}`}>Call Routing</h1>
            <p className={text2}>Visual flows that decide what happens when your numbers ring</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setIvrModal('template')} className={`px-4 py-2.5 rounded-xl border border-teal-400/40 text-teal-400 text-sm font-semibold hover:bg-teal-400/10`} data-testid="create-from-template-btn">Templates</button>
            <button onClick={() => setIvrModal('ai')} className={`px-4 py-2.5 rounded-xl border border-violet-400/40 text-violet-400 text-sm font-semibold hover:bg-violet-400/10`} data-testid="create-ai-ivr-btn">Build with AI</button>
            <button onClick={() => setShowNewFlow(true)}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-teal-400 text-[#08090d] text-sm font-bold hover:bg-teal-500 transition shadow-lg shadow-teal-500/15"
              data-testid="create-flow-btn">
              <Plus className="w-4 h-4" /> New Flow
            </button>
          </div>
        </div>

        {/* Search / Filters */}
        {flows.length > 0 && (
          <div className={`${bg} backdrop-blur-xl rounded-2xl border ${border} p-4 mb-6`}>
            <div className="flex flex-col lg:flex-row gap-3">
              <div className="flex-1 relative">
                <Search size={15} className={`absolute left-3 top-1/2 -translate-y-1/2 ${text2}`} />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or phone number…"
                  className={`w-full pl-9 pr-4 py-2.5 rounded-xl text-sm ${inputBg} border ${border} ${text1} placeholder-gray-500 focus:outline-none focus:border-teal-400/50`}
                  data-testid="search-flows" />
              </div>
              <div className="flex gap-1.5">
                {[
                  { key: 'all', label: `All (${flows.length})` },
                  { key: 'active', label: `Active (${flows.filter(f => f.enabled).length})` },
                  { key: 'inactive', label: `Inactive (${flows.filter(f => !f.enabled).length})` },
                ].map(f => (
                  <button key={f.key}
                    onClick={() => setStatusFilter(f.key)}
                    className={`px-3.5 py-2 rounded-xl text-xs font-medium transition-all ${
                      statusFilter === f.key
                        ? 'bg-teal-400/10 text-teal-300 border border-teal-400/30'
                        : `${inputBg} ${text2} border ${border} hover:bg-white/10`
                    }`}
                    data-testid={`filter-${f.key}`}>
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Empty */}
        {flows.length === 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className={`${bg} backdrop-blur-xl border ${border} rounded-2xl p-16 text-center`}>
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-400/15 to-emerald-400/10 flex items-center justify-center mx-auto mb-4">
              <GitBranch className="w-8 h-8 text-teal-300" />
            </div>
            <h3 className={`text-lg font-semibold ${text1} mb-1`}>No routing flows yet</h3>
            <p className={`text-sm ${text2} max-w-md mx-auto mb-6`}>
              Build your first flow on the visual canvas — drag nodes for menus, time-of-day routing, AI agents, transfers, and voicemail.
            </p>
            <button onClick={() => setShowNewFlow(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-teal-400 text-[#08090d] text-sm font-bold hover:bg-teal-500 transition"
              data-testid="create-first-flow-btn">
              <Plus className="w-4 h-4" /> Create Your First Flow
            </button>
          </motion.div>
        )}

        {/* Flows table */}
        {filtered.length > 0 && (
          <div className={`${bg} backdrop-blur-xl rounded-2xl border ${border} overflow-hidden`}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={`border-b ${border} ${isDark ? 'bg-white/[0.03]' : 'bg-gray-50'}`}>
                    {['Flow', 'Phone Number', 'Nodes', 'Status', ''].map(h => (
                      <th key={h} className={`px-5 py-3 text-left text-[11px] font-medium ${text2} uppercase tracking-wider`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((flow) => {
                    const nodeCount = (flow.nodes || []).length;
                    return (
                      <tr key={flow.flow_id}
                        onClick={() => openCanvas(flow)}
                        className={`border-b ${border} hover:bg-teal-400/[0.04] transition-colors group cursor-pointer`}
                        data-testid={`flow-row-${flow.flow_id}`}>
                        {/* Name */}
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                              flow.enabled ? 'bg-gradient-to-br from-teal-400/15 to-emerald-400/10 border border-teal-400/25' : isDark ? 'bg-white/[0.03] border border-white/5' : 'bg-gray-100'
                            }`}>
                              <GitBranch className={`w-4 h-4 ${flow.enabled ? 'text-teal-300' : 'text-gray-500'}`} />
                            </div>
                            <div>
                              <div className={`text-sm font-semibold ${text1}`}>{flow.name}</div>
                              <div className="text-[10px] text-gray-500 mt-0.5 group-hover:text-teal-300 transition flex items-center gap-1">
                                Open canvas <ArrowRight size={9} />
                              </div>
                            </div>
                          </div>
                        </td>
                        {/* Phone */}
                        <td className="px-5 py-3.5">
                          <div className={`text-sm font-mono flex items-center gap-2 ${text2}`}>
                            <Phone size={12} /> {flow.phone_number || '—'}
                          </div>
                        </td>
                        {/* Node count */}
                        <td className="px-5 py-3.5">
                          <span className={`inline-flex items-center gap-1 text-sm ${text2}`}>
                            {nodeCount > 0 ? <span className="text-teal-300 font-semibold">{nodeCount}</span> : <span className="text-gray-500">empty</span>}
                            <span>node{nodeCount === 1 ? '' : 's'}</span>
                          </span>
                        </td>
                        {/* Status */}
                        <td className="px-5 py-3.5">
                          <button onClick={(e) => handleToggle(flow, e)} data-testid={`toggle-flow-${flow.flow_id}`}>
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold cursor-pointer transition-all ${
                              flow.enabled
                                ? 'bg-emerald-400/10 text-emerald-300 hover:bg-emerald-400/20'
                                : isDark ? 'bg-white/[0.03] text-gray-500 hover:bg-white/[0.06]' : 'bg-gray-100 text-gray-400'
                            }`}>
                              {flow.enabled ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                              {flow.enabled ? 'Active' : 'Inactive'}
                            </span>
                          </button>
                        </td>
                        {/* Actions */}
                        <td className="px-5 py-3.5 text-right">
                          <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={(e) => handleDelete(flow, e)}
                              className="p-1.5 rounded-lg transition hover:bg-rose-500/10 text-rose-400" title="Delete"
                              data-testid={`delete-flow-${flow.flow_id}`}>
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {ivrModal && (
        <IvrCreateModal api={api} subAccountId={subAccountId} mode={ivrModal} onClose={() => setIvrModal(null)}
          onCreated={(r) => { setIvrModal(null); navigate(`/workspace/${workspaceId}/sub-account/${subAccountId}/call-routing/${r.flow_id}/canvas`); }} />
      )}
      {showNewFlow && (
        <NewFlowModal
          phoneNumbers={phoneNumbers}
          onClose={() => setShowNewFlow(false)}
          onCreate={handleCreate}
          isDark={isDark}
        />
      )}
    </div>
  );
};

export default function CallRoutingPage() {
  return (
    <SubAccountLayout>
      <CallRoutingContent />
    </SubAccountLayout>
  );
}
